import React, { useContext, useRef } from 'react';
import Router from 'next/router';
import { useDispatch } from 'react-redux';

import { AuthContext } from 'src/contexts/authContext';
import { EmailInput, PasswordInput } from 'src/components/common/form/input';

import PrimaryButton from 'src/components/common/buttons/PrimaryButton';
import Row from 'src/components/common/grid/Row';
import {
    authValidationRules,
    referralValidationRules,
} from 'src/libs/validation';
import { useForm } from 'react-hook-form';
import { VERIFY_EMAIL_PAGE_URL } from 'src/constants/links';
import useServerErrorHandler from 'src/hooks/useServerErrorHandler';
import { endLoading, startLoading } from 'src/store/slices/loadingSlice';

type TProps = { [x: string]: any };

const SignUpByEmail: React.FunctionComponent<{}> = () => {
    const dispatch = useDispatch();
    const newPassword = useRef({});
    const { signUpUserWithEmail } = useContext(AuthContext);
    const [handleServerError] = useServerErrorHandler();
    const {
        register,
        handleSubmit,
        watch,
        formState: { errors, isValid },
    } = useForm({
        mode: 'all',
    });

    newPassword.current = watch('newPassword', '');

    const onSubmit = async ({ email, newPassword }: TProps) => {
        dispatch(startLoading());
        signUpUserWithEmail(email, newPassword)
            .then(() => {
                Router.push(VERIFY_EMAIL_PAGE_URL);
            })
            .catch((err) => handleServerError(err))
            .finally(() => dispatch(endLoading()));
    };

    return (
        <div className="mb-[1.875rem]">
            <form onSubmit={handleSubmit(onSubmit)}>
                <Row>
                    <div className="my-[1.875rem] w-full">
                        <div className="mt-2">
                            <EmailInput
                                className="kyc-text-field"
                                placeholder="Email"
                                registerReactHookInput={register(
                                    'email',
                                    referralValidationRules.email,
                                )}
                                error={errors.email}
                            />
                        </div>
                    </div>
                </Row>
                <Row>
                    <div className="mb-[1.875rem] w-full">
                        <div className="mt-1">
                            <PasswordInput
                                autoComplete="on"
                                className="kyc-text-field w-full"
                                placeholder="New Password"
                                registerReactHookInput={register(
                                    'newPassword',
                                    authValidationRules.newPassword,
                                )}
                                error={errors.newPassword}
                            />
                        </div>
                    </div>
                </Row>
                <Row>
                    <div className="mb-[1.875rem] w-full">
                        <div className="mt-1">
                            <PasswordInput
                                autoComplete="on"
                                className="kyc-text-field"
                                placeholder="Confirm New Password"
                                registerReactHookInput={register(
                                    'confirmNewPassword',
                                    {
                                        ...authValidationRules.confirmNewPassword,
                                        validate: (value) =>
                                            newPassword.current === value ||
                                            'The passwords do not match',
                                    },
                                )}
                                error={errors.confirmNewPassword}
                            />
                        </div>
                    </div>
                </Row>

                <div className="flex md:w-1/3 md:items-end pr-0 mb-[30px] md:min-w-[11rem]">
                    <PrimaryButton
                        label="Sign Up"
                        disabled={!isValid}
                        paddingX="px-[2rem]"
                        margin="mb-0 mr-0"
                        width="w-full"
                    />
                </div>
                <div className="mb-[3.1rem]"></div>
            </form>
        </div>
    );
};

export default SignUpByEmail;
